import React, { useEffect, useRef, useState } from 'react';
import { db } from '../firebase';
import { doc, updateDoc } from '../firestore-compat';
import './Vidro.css';

// Visual "Liquid Glass" (vidro estilo iPhone) — opcional: cada pessoa escolhe no botão ✨ Visual da barra do topo.
// Fica no cadastro do usuário (users.data.preferencias.visual) — vale em qualquer computador — e numa cópia
// local para a tela já abrir certa. Os painéis das TVs ficam sempre no padrão (o MainLayout não liga neles).

export const FUNDOS = [
  { id: 'quality', nome: 'Verde Quality', amostra: 'linear-gradient(135deg, #3fd3bd, #4f7cf5 60%, #ff7aa8)' },
  { id: 'aurora', nome: 'Aurora', amostra: 'linear-gradient(135deg, #b69cff, #ff8cc6 55%, #49e0b0)' },
];
const PADRAO = { vidro: false, fundo: 'quality' };
const CHAVE_LOCAL = 'quality_visual';

function lerLocal() {
  try { return { ...PADRAO, ...(JSON.parse(localStorage.getItem(CHAVE_LOCAL) || 'null') || {}) }; } catch { return { ...PADRAO }; }
}

let atual = lerLocal();
const ouvintes = new Set();
function publicar(v) {
  atual = { vidro: !!v.vidro, fundo: FUNDOS.some(f => f.id === v.fundo) ? v.fundo : PADRAO.fundo };
  try { localStorage.setItem(CHAVE_LOCAL, JSON.stringify(atual)); } catch { /* sem armazenamento */ }
  ouvintes.forEach(fn => fn(atual));
}

/** Visual escolhido pelo usuário logado: { vidro, fundo } */
export function useVisual() {
  const [v, setV] = useState(atual);
  useEffect(() => {
    ouvintes.add(setV);
    setV(atual);
    return () => { ouvintes.delete(setV); };
  }, []);
  return v;
}

let escolhidoEm = 0; // hora da última troca feita aqui (o cadastro pode ainda não ter gravado)

/** Chamado pela TopBar com o que veio do cadastro (sem nada salvo = padrão, para não herdar a escolha de quem usou o computador antes). */
export function carregarVisualDoCadastro(salvo) {
  if (Date.now() - escolhidoEm < 10000) return;
  publicar(salvo && typeof salvo.vidro === 'boolean' ? salvo : PADRAO);
}

/** Liga/desliga a classe do body, a lente e o brilho dos blocos. */
export function useAplicarVidro(ligado, fundo) {
  useEffect(() => {
    const b = document.body;
    b.classList.toggle('lg-vidro', ligado);
    if (ligado) b.dataset.lgFundo = fundo; else delete b.dataset.lgFundo;
    return () => { b.classList.remove('lg-vidro'); delete b.dataset.lgFundo; };
  }, [ligado, fundo]);

  // brilho que acompanha o mouse nos blocos da tela inicial
  useEffect(() => {
    if (!ligado) return undefined;
    const mover = e => {
      const el = e.target.closest?.('.hm-bloco');
      if (!el) return;
      const r = el.getBoundingClientRect();
      el.style.setProperty('--lg-mx', `${e.clientX - r.left}px`);
      el.style.setProperty('--lg-my', `${e.clientY - r.top}px`);
    };
    document.addEventListener('pointermove', mover);
    return () => document.removeEventListener('pointermove', mover);
  }, [ligado]);

  useLente(ligado);
}

/** Fundo colorido que aparece atrás do vidro. */
export function FundoVidro() {
  return (
    <div className="lg-fundo" aria-hidden="true">
      <i className="lg-f1" /><i className="lg-f2" /><i className="lg-f3" /><i className="lg-f4" /><i className="lg-f5" />
    </div>
  );
}

// ── Lente: as bordas do vidro entortam o que está atrás ─────────────────────────────────────────────
// Só Chrome/Edge aceitam filtro SVG no backdrop-filter; nos outros navegadores fica só o desfoque do Vidro.css.
const SVG_NS = 'http://www.w3.org/2000/svg';
const TEM_LENTE = typeof navigator !== 'undefined' && /Chrome\/\d+/.test(navigator.userAgent);
const LENTES = [
  ['.sb',            { blur: 24, sat: 170, raio: 26, borda: 22, desloc: 12 }],
  ['.top-bar',       { blur: 12, sat: 180, raio: 22, borda: 18, desloc: 12 }],
  ['.hm-saudacao',   { blur: 20, sat: 160, raio: 24, borda: 20, desloc: 12 }],
  ['.hm-bloco',      { blur: 16, sat: 170, raio: 20, borda: 18, desloc: 10 }],
  ['.modal-content', { blur: 28, sat: 180, raio: 22, borda: 22, desloc: 12 }],
];

// Mapa de deslocamento: vermelho = desvio horizontal, verde = vertical (128 = parado).
// Perto da borda o ponto "puxa" a imagem de dentro, como a beirada de uma lente.
function mapaDeslocamento(w, h, raio, borda) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d');
  const img = g.createImageData(w, h);
  const d = img.data;
  const hx = w / 2, hy = h / 2, r = Math.min(raio, hx, hy);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const px = x + 0.5 - hx, py = y + 0.5 - hy;
      const qx = Math.abs(px) - (hx - r), qy = Math.abs(py) - (hy - r);
      let dist, nx, ny;
      if (qx > 0 && qy > 0) { const l = Math.hypot(qx, qy) || 1; dist = r - l; nx = (qx / l) * Math.sign(px); ny = (qy / l) * Math.sign(py); }
      else if (qx > qy) { dist = r - qx; nx = Math.sign(px); ny = 0; }
      else { dist = r - qy; nx = 0; ny = Math.sign(py); }
      let k = 0;
      if (dist > 0 && dist < borda) { const t = 1 - dist / borda; k = t * t; }
      const i = (y * w + x) * 4;
      d[i] = 128 - nx * k * 127;
      d[i + 1] = 128 - ny * k * 127;
      d[i + 2] = 128;
      d[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  return c.toDataURL();
}

function useLente(ligado) {
  useEffect(() => {
    if (!ligado || !TEM_LENTE || typeof ResizeObserver === 'undefined') return undefined;
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.setAttribute('aria-hidden', 'true');
    svg.style.position = 'absolute';
    const defs = document.createElementNS(SVG_NS, 'defs');
    svg.append(defs);
    document.body.append(svg);

    const filtros = new Map(); // elemento → { id, w, h, filtro, valor }
    let seq = 0;
    let tempo = null;
    const agendar = () => { clearTimeout(tempo); tempo = setTimeout(varrer, 150); };
    const ro = new ResizeObserver(agendar);

    function aplicar(el, cfg) {
      const w = Math.round(el.offsetWidth), h = Math.round(el.offsetHeight);
      if (!w || !h) return;
      let info = filtros.get(el);
      if (!info) { ro.observe(el); info = {}; filtros.set(el, info); }
      if (info.w !== w || info.h !== h) {
        if (info.filtro) info.filtro.remove();
        const id = `lg-lente-${++seq}`;
        // sem x/y/width/height no <filter>: com a região fixa o Chrome perdia o desfoque no canto de baixo dos blocos
        const f = document.createElementNS(SVG_NS, 'filter');
        f.setAttribute('id', id);
        f.setAttribute('color-interpolation-filters', 'sRGB');
        const im = document.createElementNS(SVG_NS, 'feImage');
        im.setAttribute('href', mapaDeslocamento(w, h, cfg.raio, cfg.borda));
        Object.entries({ x: 0, y: 0, width: w, height: h, preserveAspectRatio: 'none', result: 'mapa' }).forEach(([k, v]) => im.setAttribute(k, v));
        const dm = document.createElementNS(SVG_NS, 'feDisplacementMap');
        Object.entries({ in: 'SourceGraphic', in2: 'mapa', scale: cfg.desloc * 2, xChannelSelector: 'R', yChannelSelector: 'G' }).forEach(([k, v]) => dm.setAttribute(k, v));
        f.append(im, dm);
        defs.append(f);
        Object.assign(info, { id, w, h, filtro: f });
      }
      // lente primeiro, desfoque depois (na ordem contrária o Chrome cortava parte do desfoque)
      const valor = `url(#${info.id}) blur(${cfg.blur}px) saturate(${cfg.sat}%)`;
      if (info.valor !== valor) { el.style.backdropFilter = valor; info.valor = valor; }
    }

    function varrer() {
      filtros.forEach((info, el) => {
        if (el.isConnected) return;
        info.filtro?.remove();
        ro.unobserve(el);
        filtros.delete(el);
      });
      LENTES.forEach(([sel, cfg]) => document.querySelectorAll(sel).forEach(el => aplicar(el, cfg)));
    }

    // telas e janelas entram e saem o tempo todo (algumas janelas vão direto no body): procura de novo quando a página muda
    const mo = new MutationObserver(agendar);
    mo.observe(document.body, { childList: true, subtree: true });
    varrer();

    return () => {
      clearTimeout(tempo);
      mo.disconnect();
      ro.disconnect();
      filtros.forEach((info, el) => { el.style.backdropFilter = ''; });
      svg.remove();
    };
  }, [ligado]);
}

/** Botão ✨ Visual da barra do topo, com a escolha Padrão × Liquid Glass e o fundo. */
export function SeletorVisual({ docId }) {
  const v = useVisual();
  const [aberto, setAberto] = useState(false);
  const caixa = useRef(null);
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = e => { if (caixa.current && !caixa.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const escolher = mudanca => {
    const novo = { ...v, ...mudanca };
    escolhidoEm = Date.now();
    publicar(novo);
    // só a chave do visual: não mexe nas outras preferências (cores da Minha Agenda / E-mail)
    if (docId) updateDoc(doc(db, 'users', docId), { 'preferencias.visual': { vidro: novo.vidro, fundo: novo.fundo } }).catch(() => {});
  };

  return (
    <div className="lg-rel" ref={caixa}>
      <button type="button" className="tb-sair" onClick={() => setAberto(a => !a)} title="Escolher o visual do Quality">✨ Visual</button>
      {aberto && (
        <div className="lg-painel">
          <div className="lg-titulo">Visual do Quality <span>só para você</span></div>
          <div className="lg-opcoes">
            <button type="button" className={`lg-opcao ${!v.vidro ? 'ativo' : ''}`} onClick={() => escolher({ vidro: false })}>
              <span className="lg-amostra lg-amostra-padrao"><i /></span>
              <b>Padrão</b><small>como sempre foi</small>
            </button>
            <button type="button" className={`lg-opcao ${v.vidro ? 'ativo' : ''}`} onClick={() => escolher({ vidro: true })}>
              <span className="lg-amostra lg-amostra-vidro"><i /></span>
              <b>Liquid Glass</b><small>vidro, estilo iPhone</small>
            </button>
          </div>
          {v.vidro && (
            <>
              <div className="lg-rotulo">Fundo</div>
              <div className="lg-fundos">
                {FUNDOS.map(f => (
                  <button type="button" key={f.id} className={`lg-fundo-op ${v.fundo === f.id ? 'ativo' : ''}`} onClick={() => escolher({ fundo: f.id })}>
                    <span style={{ background: f.amostra }} />{f.nome}
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="lg-nota">Fica salvo no seu usuário e vale em qualquer computador. Se a tela ficar lenta, volte para o Padrão. Os painéis das TVs não mudam.</div>
        </div>
      )}
    </div>
  );
}
